import Hls from 'hls.js';
import EventBus, { Event } from '@/core/event-bus';
import { HlsErrorHandler, emitToClient } from '.';
import SmvpErrorHandler, { ErrorData } from './smvp-error-handler';

class DefaultErrorHandling {
    constructor(
        private hls: Hls,
        private eventBus: EventBus,
        private smvpErrorHandler: SmvpErrorHandler,
    ) { }

    public handleError(event: string, data: ErrorData) {
        if (data.fatal) {
            this.handleFatal(event, data);
        } else {
            this.handleNonFatal(event, data);
        }

        emitToClient(this.hls, this.eventBus, event, data);
    }

    private handleFatal(event: string, data: ErrorData) {
        console.debug('[DefaultErrorHandler] starting fatal timeout on handleFatal', { event, data });
        this.smvpErrorHandler.startFatalTimeoutHandler(event, data);
        switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
                return this.handleNetworkError(event, data);
            case Hls.ErrorTypes.MEDIA_ERROR:
                return this.handleMediaError(event, data);
            default:
                return this.handleUnknownError(event, data);
        }
    }

    private handleNonFatal(event: string, data: ErrorData) {
        log(event, data);
    }

    private handleNetworkError(event: string, data: ErrorData) {
        log(event, data);

        if (data?.frag?.type === 'subtitle') {
            console.error('[DefaultErrorHandler] fatal network error on subtitle, disabling subtitles', data.error);
            this.eventBus.emit(Event.disableSubtitles);
        }

        this.hls.startLoad();
    }

    private handleMediaError(event: string, data: ErrorData) {
        log(event, data);

        this.hls.recoverMediaError();
    }

    private handleUnknownError(event: string, data: ErrorData) {
        // can no longer recover
        log(event, data, false);
        this.smvpErrorHandler.smvpFatalEmit(event, data);
    }
}

function log(
    event: string,
    data: ErrorData,
    canRecover = true
) {
    const fatalMsg = data.fatal ? ' fatal' : '';
    const recoverMsg = canRecover ? 'Trying to recover' : 'Cannot recover';
    const logger = data.fatal ? console.error : console.warn;

    logger(
        `[Hls]${fatalMsg} ${data?.details} encountered. ${recoverMsg}`,
        { event, data }
    );
}

export default function onErrorDefault(hls: Hls, eventBus: EventBus, smvpErrorHandler: SmvpErrorHandler): HlsErrorHandler {
    const defaultErrorHandling = new DefaultErrorHandling(hls, eventBus, smvpErrorHandler);

    return defaultErrorHandling.handleError.bind(defaultErrorHandling);
}
